import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function GnulChapter3() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <div className="calculus-hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(8,8,8,0.5) 0%, rgba(8,8,8,0.8) 60%, rgba(8,8,8,1) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/assets/gnul-hero.png)', backgroundSize: 'cover', backgroundPosition: 'center 20%', zIndex: 1 }} />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">The Chemistry of Leadership &middot; Chapter 3</span>
          <h1>The Landing</h1>
          <p className="calculus-hero-sub">He thought he was hired. He did not know he was inherited.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Empty Plane */}
          <section className="calc-section reveal">
            <h2>The Empty Plane</h2>
            <p className="calc-lead">
              The door closes behind him and the sound changes. Everything changes. The
              terminal, the crowd, the wall of ambient chemistry, gone. Replaced by silence
              so complete it has texture. The hum of engines. The whisper of pressurized air.
              Nothing else.
            </p>
            <p>
              He is alone.
            </p>
            <p>
              The cabin is larger than his bedroom at the cabin. Leather seats the color of
              cream. A table with a glass of water already poured. Windows that curve with the
              fuselage. He touches the armrest and the leather is softer than anything he has
              ever touched. He does not know if this is normal. He has never been on a plane.
              Maybe all planes are like this. Maybe none of them are.
            </p>
            <p>
              He sits. The seat adjusts to him. He did not press anything. It just moved. His
              weight, his height, the angle of his back, something measured all of it and
              responded without being asked. He stares at the seat for a long moment because
              it is the first object that has ever accommodated his size without him having to
              ask or adjust.
            </p>
            <p>
              The plane begins to move. No announcement. No safety demonstration. No flight
              attendant. Just motion. The runway, the acceleration, the lift. His stomach
              drops and his hands grip the armrests and then the ground is gone and there is
              nothing below him but air and he is flying and he does not know if the tight
              feeling in his chest is fear or wonder or both.
            </p>
          </section>

          <Pulse />

          {/* The Phone */}
          <section className="calc-section reveal">
            <h2>The Phone</h2>
            <p>
              He pulls it out. The screen is bright in the dim cabin. He starts pressing
              things. Exploring. Button by button. Icon by icon. The way a child explores,
              not with purpose but with the dopamine of not knowing what comes next.
            </p>
            <p>
              Email. Hundreds of unread messages. Subject lines he does not understand.
              "Q3 Transit Corridor Projections." "Vendor Compliance Update." "RE: RE: RE:
              Board Composition." He closes it.
            </p>
            <p>
              Calendar. Blocked solid for the next two weeks. Meetings with names and
              titles and acronyms. He does not know what a "stakeholder alignment session"
              is. He closes it.
            </p>
            <p>
              Slack. He has already been there. He leaves it alone.
            </p>
            <p>
              Then he taps an icon he does not recognize. A small gold shield. The screen
              goes dark for a moment. Then a prompt appears.
            </p>
            <p>
              <em>Facial recognition required.</em>
            </p>
            <p>
              He holds the phone up to his face. The camera activates. A small circle
              appears on the screen, scanning. He watches it trace the outline of his
              features. His tusks. His brow. The bridge of his nose. The shape of his
              jaw. Someone, somewhere, programmed this. Someone fed his photograph into a
              biometric system and told it: this face is authorized.
            </p>
            <p>
              The screen unlocks.
            </p>
            <p>
              A number.
            </p>
            <p>
              He does not understand it at first. There are too many digits. He counts
              them. Eleven digits before the decimal. He counts again. He has never seen
              a number this large outside of a textbook chapter on astronomical distances.
            </p>
            <p>
              $200,000,000,000.00
            </p>
            <p>
              The label above it says: <em>Gnul Irrevocable Trust</em>.
            </p>
            <p>
              His name. His trust. Not a company account. Not an operating budget. A trust
              with his name on it and a number that has more zeros than he can hold in his
              head at the same time.
            </p>
            <p>
              He stares at it. His dopamine fires and then stalls because there is no
              prediction framework for this. He has never bought anything. His caretaker
              bought everything. He has never held cash, never swiped a card, never
              understood what a dollar means in the context of a life. He knows the
              number is large. He does not know what it means to have it. He does not
              know if this is part of the job. He does not know if all CEOs have this.
              He does not know anything except that his face unlocked it and his name is
              on it and the plane he is sitting in is very quiet and very empty and the
              number will not stop staring back at him.
            </p>
            <p>
              He closes the app. He puts the phone on the table. He looks out the window
              at clouds he has never seen from above.
            </p>
            <p>
              The phone rings.
            </p>
            <p>
              The sound and the vibration together hit him like a jolt of electricity. He
              has never received a phone call. The device buzzes against the table and
              screams a ringtone he did not choose and his startle reflex fires before his
              prefrontal cortex can intervene. His hand jerks. The phone launches off the
              table, hits the armrest, and clatters to the cabin floor.
            </p>
            <p>
              He stares at it on the carpet. Buzzing. Ringing. The screen says UNKNOWN.
            </p>
            <p>
              He picks it up. His hands are shaking. He answers.
            </p>
          </section>

          <Pulse />

          {/* The Call */}
          <section className="calc-section reveal">
            <h2>The Call</h2>
            <p>
              "Gnul." A voice. Male. Old. The kind of old that sounds like gravel wrapped
              in silk. Low register. Impossibly steady. No rush. No warmth. No cold. The
              flattest frequency he has ever heard from a living thing.
            </p>
            <p>
              "My name is Aldric Thane. I am the attorney who drafted your appointment. I
              need twelve minutes of your time before you land."
            </p>
            <p>
              "Okay."
            </p>
            <p>
              "What I am about to tell you will restructure your understanding of your
              position. I need you to listen without responding until I finish. Can you
              do that?"
            </p>
            <p>
              His cortisol spikes. Not from the tone. From the absence of one. Aldric
              Thane's voice carries nothing. No prediction. No warmth. No threat. It is
              the vocal equivalent of a blank page, and blank pages terrify the amygdala
              because it cannot categorize what it cannot read.
            </p>
            <p>
              "Yes."
            </p>
          </section>

          <Pulse />

          {/* Twelve Minutes */}
          <section className="calc-section reveal">
            <h2>Twelve Minutes</h2>
            <p>
              "Æther was founded forty-three years ago by a woman named Seraphine Vael.
              She built the first viable intergalactic transit system from a shed in
              northern Canada with six employees and a hypothesis about folded space that
              three universities had rejected. She was right. They were wrong. The company
              is now the largest private entity in human history."
            </p>
            <p>
              Gnul listens.
            </p>
            <p>
              "Seraphine died eight months ago. She had no spouse. No children. No heirs
              in the traditional sense. Her will was eleven pages long and contained one
              operational instruction."
            </p>
            <p>
              Gnul's breathing has stopped. He restarts it manually. Four seconds in.
            </p>
            <p>
              "The instruction was to post a job listing. She wrote the listing herself.
              She specified the language. 'Build culture across distance. Inspire without
              proximity. Lead through communication rather than presence.' She specified
              that the listing should remain open for ninety days and that the successful
              applicant should be appointed not as CEO but as sole owner of Æther, its
              subsidiaries, its intellectual property, and its liquid assets."
            </p>
            <p>
              Seven seconds hold.
            </p>
            <p>
              "You were the only applicant who listed 'experience: none' and submitted
              anyway. You were the only applicant whose education was entirely in
              neurochemistry and behavioral pharmacology. You were the only applicant
              who did not lie about anything. Seraphine's selection criteria, which she
              left in a sealed envelope to be opened after the listing closed, contained
              three requirements. One: the applicant must have no industry experience.
              Two: the applicant must understand the science of human connection. Three:
              the applicant must be honest about what they do not know."
            </p>
            <p>
              Eight seconds out.
            </p>
            <p>
              "You met all three. You are not the CEO of Æther, Gnul. You are the owner.
              The 200 billion in the account you accessed this morning is not the company's
              operating fund. It is yours. The aircraft you are sitting in is yours. The
              building you are flying toward is yours. Every ship, every route, every
              patent, every contract, every employee. Yours."
            </p>
            <p>
              Silence. The engines hum. The air recycler clicks. Somewhere far below, the
              world is the size of a map.
            </p>
            <p>
              "Do you have questions?"
            </p>
            <p>
              Gnul opens his mouth. Closes it. Opens it again. His hands are shaking for
              the third time today. The prediction error is so massive that his dopamine
              system has overloaded and his cortisol has joined it and the two are fighting
              for the same neurochemical real estate and neither is winning.
            </p>
            <p>
              "Who was she to me?"
            </p>
            <p>
              A pause. The first pause from Aldric Thane. The first break in the flat
              frequency.
            </p>
            <p>
              "I don't know. She never said. She simply wrote the listing and asked that
              it be posted. She died eleven days later."
            </p>
            <p>
              Gnul stares out the window. The clouds are below him. Everything is below
              him. He owns a company that travels between stars and he has never traveled
              between cities.
            </p>
            <p>
              "One more question."
            </p>
            <p>
              "Yes."
            </p>
            <p>
              "Who is Caelan Voss?"
            </p>
          </section>

          <Pulse />

          {/* The Chief of Staff */}
          <section className="calc-section reveal">
            <h2>The Chief of Staff</h2>
            <p>
              Another pause. Longer this time. When Aldric speaks again, his flat
              frequency shifts for the first time. Not warmer. Careful.
            </p>
            <p>
              "Caelan Voss has been Chief of Staff at Æther for nine years. He was
              Seraphine's operational right hand. He ran the company during her final
              illness. He expected to inherit. He did not."
            </p>
            <p>
              Gnul reads the space between the words. His caretaker taught him that.
              The information is not in what Aldric says. It is in what Aldric is
              choosing not to say.
            </p>
            <p>
              "He sent a message to the entire company twenty minutes ago. Report to
              the office tomorrow at 8 AM or lose your job."
            </p>
            <p>
              "I am aware."
            </p>
            <p>
              "Is he allowed to do that?"
            </p>
            <p>
              "He was allowed to do that when Seraphine was alive because Seraphine
              trusted him. Whether he is allowed to do that now is a question only you
              can answer. You are the owner."
            </p>
            <p>
              Gnul sits with it. The phone is warm against his ear. The clouds scroll
              past below. Four hundred people just received a threat from a man who
              thinks he runs a company that now belongs to someone he has never met.
              Someone whose face he has seen only in a photograph. Someone he probably
              made a meme about.
            </p>
            <p>
              "Mr. Thane."
            </p>
            <p>
              "Yes."
            </p>
            <p>
              "Does Caelan Voss know that I am the owner and not just the CEO?"
            </p>
            <p>
              "No. Only three people knew the full terms of Seraphine's will. Myself.
              The executor. And now you."
            </p>
            <p>
              Gnul smiles. It is a different smile from the one Loriann gave him. That
              was warmth. This is something colder. Something strategic. Something his
              caretaker would recognize from the marketing textbooks on the bottom shelf.
            </p>
            <p>
              "Keep it that way."
            </p>
            <p>
              "Understood."
            </p>
            <p>
              "One more thing. The message from Voss. Everyone in the office at 8 AM."
            </p>
            <p>
              "Yes?"
            </p>
            <p>
              "I will be there at 7."
            </p>
          </section>

          <Pulse />

          {/* The Notification */}
          <section className="calc-section reveal">
            <h2>The Slack</h2>
            <p>
              He hangs up. The phone feels different in his hand now. Heavier. Not from
              the weight. From the knowing.
            </p>
            <p>
              He opens Slack. The channel is on fire. Voss's message has detonated. The
              feed is moving so fast he can barely read it.
            </p>
            <p>
              <em>"Is this real? We're fully remote. We've ALWAYS been remote."</em>
            </p>
            <p>
              <em>"I live in another state. How am I supposed to be there by 8?"</em>
            </p>
            <p>
              <em>"Does this come from the new CEO or from Voss?"</em>
            </p>
            <p>
              <em>"My kid has school. I can't just..."</em>
            </p>
            <p>
              <em>"This is why people quit."</em>
            </p>
            <p>
              <em>"Voss has been pulling this since Seraphine got sick. He thinks
              he's in charge."</em>
            </p>
            <p>
              <em>"He IS in charge. The new guy hasn't even shown up yet."</em>
            </p>
            <p>
              And then, like a wall:
            </p>
            <p>
              <em>"I would wait for the CEO to confirm before you panic. Voss does
              not speak for everyone."</em>
            </p>
            <p>
              Loriann. Again. The same steady frequency. The same refusal to participate
              in the chemistry of the room. While four hundred people are dumping cortisol
              into the channel, she is the one node in the system whose signal is not
              contaminated.
            </p>
            <p>
              He watches the feed for three more minutes. He reads every message. He
              catalogs the chemistry. Fear. Anger. Resentment. Confusion. A few jokes
              from people whose cortisol expresses as humor. A few silences from people
              whose cortisol expresses as withdrawal. And underneath all of it, the
              question every person is asking without typing it:
            </p>
            <p>
              <em>Who is this new CEO and will he protect us from Voss?</em>
            </p>
            <p>
              He does not respond. Not in the channel. Not to Loriann. Not to anyone.
            </p>
            <p>
              But he opens a new app on the phone. Notes. He starts a list.
            </p>
            <p>
              The first entry: <em>Caelan Voss. Nine years. Expected to inherit.
              Did not. Sent fear as first signal. Timing: while owner was airborne
              and unreachable. Strategy: establish authority before new leadership
              lands.</em>
            </p>
            <p>
              The second entry: <em>Loriann. Consistent. Uncontaminated frequency.
              Defended before evidence. Defended again under pressure. Does not mirror
              ambient cortisol. Rare.</em>
            </p>
            <p>
              The third entry: <em>The company is afraid. Not of me. Of Voss. They
              have been afraid of Voss for months. This is not new. This is chronic.
              Cortisol baseline of the entire organization is elevated. Seraphine's
              illness created a power vacuum and Voss filled it with fear.</em>
            </p>
            <p>
              He closes the app. He leans back. The plane begins its descent.
            </p>
            <p>
              He has never managed anyone. He has never led a meeting. He has never
              fired or hired or disciplined or praised in a professional context. He
              has a biochemistry education and a letter that says "Fly, bitch" and a
              bank account with a number he still cannot fully comprehend.
            </p>
            <p>
              But he knows one thing that Caelan Voss does not.
            </p>
            <p>
              Fear is a half-life. Cortisol peaks and decays. The threat Voss sent
              will lose potency every hour between now and 8 AM. By morning, the
              adrenaline will have cleared. The initial panic will have faded. What
              will remain is resentment: the slow, serotonin-depleting residue of
              being threatened by someone who was supposed to be on your side.
            </p>
            <p>
              Voss used cortisol as a tool. But cortisol has a half-life of ninety
              minutes. By the time those employees walk through the door tomorrow
              morning, the fear will be gone. The anger will remain.
            </p>
            <p>
              And Gnul will be standing in the lobby at 7 AM with the only weapon
              that does not decay.
            </p>
            <p>
              His voice.
            </p>
          </section>

          <Pulse />

          {/* The Descent */}
          <section className="calc-section reveal">
            <h2>The Descent</h2>
            <p>
              The plane drops below the clouds. A city appears. He has never seen a
              city from above. The grid of it. The lights. The veins of highways moving
              things and people from one point to another. From up here it looks like a
              circuit board. From up here it looks like a nervous system.
            </p>
            <p>
              His phone buzzes. A text from a number he does not recognize.
            </p>
            <p>
              <em>"Mr. Gnul. This is Caelan Voss, Chief of Staff. I trust you
              received my directive regarding tomorrow's all-hands. I look forward
              to meeting you and ensuring a smooth transition. Please let me know
              your ETA and I will arrange ground transportation."</em>
            </p>
            <p>
              He reads it three times. The frequency is different from the Slack
              message. The Slack message was a hammer. This is a handshake. The same
              man, two completely different signals, calibrated for two completely
              different audiences. To the employees: fear. To the CEO: deference.
            </p>
            <p>
              His caretaker had a word for this. She used it once, sitting at the
              kitchen table, explaining why some people's oxytocin signatures are
              unreliable. The word was <em>bilateral</em>. A person who produces
              one frequency for the people below them and another for the people
              above them. Not a liar. Something more precise. A person whose
              neurochemistry recalibrates based on the perceived power of the
              person in front of them.
            </p>
            <p>
              Gnul does not respond to the text.
            </p>
            <p>
              The plane touches down. The wheels hit the ground and the jolt runs
              through his body and for a moment, one fraction of a second, the
              vibration feels like the world reaching up to grab him.
            </p>
            <p>
              He is here. Wherever here is.
            </p>
            <p>
              The phone buzzes again. Loriann.
            </p>
            <p>
              <em>"If you're landing tonight, there's a hotel two blocks from the
              office. The Meridian. Front desk knows to expect you. Voss didn't
              arrange it. I did."</em>
            </p>
            <p>
              He stares at it. Then types:
            </p>
            <p>
              <em>"How did you know I was landing tonight?"</em>
            </p>
            <p>
              Three dots. Then:
            </p>
            <p>
              <em>"I booked the jet, sir."</em>
            </p>
            <p>
              A pause.
            </p>
            <p>
              <em>"Sorry. Gnul."</em>
            </p>
          </section>

          <Pulse />

          {/* The Science */}
          <section className="calc-section reveal">
            <h2>The Science Behind Chapter 3</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Bilateral Frequency</span>
              <code>if perceived_power(target) &gt; self &rarr; voice = deference<br />if perceived_power(target) &lt; self &rarr; voice = dominance</code>
              <span className="calc-eq-desc">A bilateral communicator produces different neurochemical signatures depending on the perceived power of the person they are addressing. This is not conscious deception. It is an autonomic recalibration. The vocal register, word choice, and pace shift based on where the speaker places themselves in the hierarchy relative to the listener. It is detectable. The same person who texts "I look forward to ensuring a smooth transition" to the CEO sent "report or lose your job" to the employees. Same person. Same hour. Two frequencies.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">Cortisol Half-Life as Leadership Limit</span>
              <code>cortisol t&frac12; = 90 min &rarr; fear-based directives lose 50% potency every 90 min</code>
              <span className="calc-eq-desc">A threat delivered at 6 PM has lost 93.75% of its cortisol impact by midnight. What remains is not fear. It is resentment: the serotonin-depleting residue of being controlled through chemistry rather than led through trust. Fear-based leadership has a built-in expiration. Trust-based leadership compounds. The math favors trust on every timescale longer than two hours.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Power Vacuum Dynamics</span>
              <code>if authority_node = absent &rarr; highest-cortisol node fills vacuum</code>
              <span className="calc-eq-desc">In the absence of a clear authority signal, organizations default to the person who produces the strongest neurochemical pressure. This is not meritocracy. It is chemistry. Caelan Voss did not earn the interim authority. He occupied the space left by Seraphine's absence by being the loudest signal in the room. The organization complied not because he was right but because compliance reduces cortisol and the system will always choose the path of least chemical resistance.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">Uncontaminated Node Value</span>
              <code>organizational_resilience &prop; count(nodes where cortisol_baseline &lt; &theta;_chronic)</code>
              <span className="calc-eq-desc">An organization's resilience is proportional to the number of people whose baseline cortisol has not been chronically elevated by the system itself. Loriann is an uncontaminated node. Her frequency does not mirror the ambient fear. She books jets and arranges hotels while the channel burns. These nodes are the most valuable people in any organization and the first ones a fear-based leader will try to neutralize, because their calm is a counter-frequency to the fear the leader depends on.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Information Asymmetry as Strategic Advantage</span>
              <code>if knowledge(owner) &gt; knowledge(opponent) AND opponent does not know &rarr; optimal move = silence</code>
              <span className="calc-eq-desc">Gnul knows he is the owner. Voss does not know Gnul knows. This asymmetry gives Gnul the ability to observe Voss's behavior without Voss adjusting for the observation. Every action Voss takes between now and the moment of revelation is unfiltered data about who Voss really is. Silence preserves the asymmetry. Responding would collapse it. The optimal strategic move is to watch.</span>
            </div>
          </section>

          <Pulse />

          {/* Navigation */}
          <section className="calc-section reveal" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <a href="/gnul/chapter-2" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>&larr; Chapter 2: The Terminal</a>
            <span style={{ color: '#333', margin: '0 2rem' }}>|</span>
            <a href="/gnul/chapter-4" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>Chapter 4: The Meridian &rarr;</a>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>Narrative Physics&trade; Leadership Development</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GnulChapter3;
